import React, { useCallback, useMemo } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import FormSubmissionsList from '@/components/forms/FormSubmissionsList'
import { FormSubmission } from '@/core/models/form.model'
import { useGetConsultationFormSubmissionsQuery } from '@/core/services/form'
import { useGetConsultationQuery } from '@/core/services/consultation'
import FormSelectionDialogContainer from '@/pages/diagnostic/containers/forms/FormSelectionDialogContainer'

interface ConsultationFormsContainerProps {
    consultationId: string
}

const ConsultationFormsContainer: React.FC<ConsultationFormsContainerProps> = ({
    consultationId
}) => {
    const { t } = useTranslation()
    const [page, setPage] = React.useState(0)
    const [pageSize, setPageSize] = React.useState(5)
    const [isFormSelectionOpen, setIsFormSelectionOpen] = React.useState(false)

    const {
        data: consultation,
        isLoading: isConsultationLoading,
        error: consultationError
    } = useGetConsultationQuery(consultationId!, {
        skip: !consultationId
    })

    const {
        data: submissionsData,
        isLoading: isSubmissionsLoading,
        error: queryError,
    } = useGetConsultationFormSubmissionsQuery(
        {
            consultationId,
            skip: page * pageSize,
            limit: pageSize,
            order: 'DESC',
            orderBy: 'createdAt',
        },
        {
            refetchOnFocus: true,
            skip: !consultationId,
            pollingInterval: 30000, // Poll every 30 seconds
        }
    )

    const handleOpenFormSelection = useCallback(() => {
        setIsFormSelectionOpen(true)
    }, [])

    const handleCloseFormSelection = useCallback(() => {
        setIsFormSelectionOpen(false)
    }, [])

    const handleFormTemplateSelect = useCallback((templateId: string) => {
        if (!consultationId) {
            console.error('No consultation ID found')
            return
        }
        if (isConsultationLoading) {
            console.error('Consultation data is still loading')
            return
        }
        if (consultationError) {
            console.error('Error loading consultation data:', consultationError)
            return
        }
        if (!consultation) {
            console.error('Consultation data not found')
            return
        }
        if (!consultation.patient?._id) {
            console.error('No patient ID found in consultation')
            return
        }

        // Open form submission in new tab with all required params
        const url = `/forms/submit/${templateId}/${consultation.patient._id}?type=consultation&objectId=${consultationId}`
        window.open(url, '_blank')
        handleCloseFormSelection()
    }, [consultation, consultationId, isConsultationLoading, consultationError])

    const handleEditDraft = useCallback((submissionId: string) => {
        const submission = submissionsData?.items.find(s => s._id === submissionId)
        if (!submission || !consultation?.patient?._id) {
            console.error('Submission or patient ID not found')
            return
        }
        // Open form submission in new tab with all required params and the submission ID
        const url = `/forms/submit/${submission.template._id}/${consultation.patient._id}/${submissionId}?type=consultation&objectId=${consultationId}`
        window.open(url, '_blank')
    }, [submissionsData?.items, consultation, consultationId])

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage)
    }, [])

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize)
        setPage(0)
    }, [])

    // Convert RTK Query error to standard Error
    const error = useMemo(() => {
        if (!queryError) return undefined
        if ('status' in queryError && typeof queryError.data === 'object' && queryError.data) {
            const errorData = queryError.data as { message?: string }
            return new Error(errorData?.message || 'An error occurred')
        }
        return new Error((queryError as Error).message || 'An error occurred')
    }, [queryError])

    if (isConsultationLoading || isSubmissionsLoading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography>{t('common.loading')}</Typography>
            </Box>
        )
    }

    if (consultationError || error) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="error">
                    {consultationError ?
                        t('common.error.loadingFailed') :
                        error?.message || t('common.error.loadingFailed')
                    }
                </Typography>
            </Box>
        )
    }

    return (
        <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                    {t('page.forms.title')}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenFormSelection}
                >
                    {t('page.forms.newForm')}
                </Button>
            </Stack>

            <Box>
                <FormSubmissionsList
                    submissions={submissionsData?.items || []}
                    totalCount={submissionsData?.totalCount || 0}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    isLoading={isSubmissionsLoading}
                    error={error}
                    onEditDraft={handleEditDraft}
                />
            </Box>

            <FormSelectionDialogContainer
                open={isFormSelectionOpen}
                onClose={handleCloseFormSelection}
                onSelect={handleFormTemplateSelect}
            />
        </Stack>
    )
}

export default ConsultationFormsContainer 